import {
  Box,
  Button,
  Grid,
  Link,
  TextField,
  Typography,
} from "@material-ui/core";
import React from "react";
import { useIntl } from "react-intl";
import CustomIcon from "../../core/custom-icon";
import { getLocalizedValue } from "../../core/lang";
import { ViewAs } from "../../corporation";
import { MeetingProps } from "./index";
import { MeetingHeader } from "./MeetingHeader";
import { MeetingLobbyFileUpload } from "./MeetingLobbyFileUpload";
import { MeetingProductAccordion } from "./MeetingProductAccordion";
type Props = {
  onDocumentUpload: (file: any) => void;
  onCommentChange: (comment: string) => void;
  onCancel: () => void;
  onConfirm: () => void;
  documents?: string[];
  comment?: string;
} & MeetingProps;

export const MeetingLobbyPanel = ({
  expert,
  participant,
  corporation,
  locale,
  date,
  onDocumentUpload,
  productName,
  productDescription,
  documents,
  comment,
  viewAs,
  onCommentChange,
  onCancel,
  onConfirm,
}: Props) => {
  const { formatMessage, formatDate } = useIntl();
  return (
    <Box>
      <MeetingHeader
        expert={viewAs === ViewAs.Expert ? participant : expert}
        corporation={corporation}
        locale={locale}
        date={date}
        title={formatMessage(
          viewAs === ViewAs.Expert
            ? {
                id: "MeetingLobbyTitleExpert",
                defaultMessage: "Willkommen zu Ihrem Termin mit {name}",
              }
            : {
                id: "MeetingLobbyTitle",
                defaultMessage: "Ihre Terminanfrage mit {name}.",
              },
          {
            name: ViewAs.Expert
              ? participant.fullName
              : getLocalizedValue(locale, corporation.corpName),
          }
        )}
        text={
          viewAs === ViewAs.Expert
            ? formatMessage(
                {
                  id: "MeetingLobbyTextExpert",
                  defaultMessage: `Im Rahmen der WindEnergy Hamburg vom 1.12. bis 4.12.2020 haben Sie 
                            für {dayName}, den {startDate} von {startTime} bis {endTime} Uhr 
                            einen Gesprächstermin mit {teilnehmer} von {firma} bestätigt. 
                            Mit klick auf "Jetzt teilnehmen" werden Sie in das Gespräch geschaltet.`,
                },
                {
                  dayName: formatDate(date.startDate, {
                    weekday: "long",
                  }),
                  startDate: formatDate(date.startDate),
                  startTime: formatDate(date.startDate, {
                    hour: "numeric",
                    minute: "numeric",
                  }),
                  endTime: formatDate(date.endDate, {
                    hour: "numeric",
                    minute: "numeric",
                  }),
                  teilnehmer: participant.fullName,
                  firma: getLocalizedValue(locale, participant.corpName),
                }
              )
            : formatMessage(
                {
                  id: "MeetingLobbyText",
                  defaultMessage: `Im Rahmen der WindEnergy Hamburg vom 1.12. bis 4.12.2020 haben Sie 
                            für {dayName}, den {startDate} von {startTime} bis {endTime} Uhr 
                            einen Gesprächstermin mit {corpName} verinbart. 
                            Präzisieren Sie ihr Anliegen und werden mit Klick auf 
                            "Teilnehmen" in das Gespräch mit {expertName} geleitet.`,
                },
                {
                  dayName: formatDate(date.startDate, {
                    weekday: "long",
                  }),
                  startDate: formatDate(date.startDate),
                  startTime: formatDate(date.startDate, {
                    hour: "numeric",
                    minute: "numeric",
                  }),
                  endTime: formatDate(date.endDate, {
                    hour: "numeric",
                    minute: "numeric",
                  }),
                  corpName: getLocalizedValue(locale, corporation.corpName),
                  expertName: expert.fullName,
                }
              )
        }
      />
      <Grid container spacing={5}>
        <Grid item md={6}>
          <Box>
            <Typography
              component="h3"
              variant="subtitle1"
              color="primary"
              gutterBottom
            >
              {viewAs === ViewAs.ActiveParticipant
                ? formatMessage({
                    id: "MeetingLobbyDocumentUpload",
                    defaultMessage: "Dokumenten Upload",
                  })
                : formatMessage({
                    id: "MeetingLobbyDocumentUploadExpert",
                    defaultMessage: "Dateien des Teilnehmers",
                  })}
            </Typography>
            <Box mt={3.5}>
              {(viewAs === ViewAs.ActiveParticipant &&
                (!documents ||
                documents.length === 0)) && (
                <MeetingLobbyFileUpload
                  onUpload={onDocumentUpload}
                  maxUpload={process.env.REACT_APP_MAX_UPLOAD ? Number.parseInt(process.env.REACT_APP_MAX_UPLOAD) : 10}
                  supportedTypes={process.env.REACT_APP_UPLOAD_EXT ? 
                  process.env.REACT_APP_UPLOAD_EXT.split(",") : ['pdf', 'jpg', 'png', 'doc', 'cad']}
                />
              )}

              {documents &&
                documents.map((d, i) => (
                  <Box key={i} mb={2}>
                    <Link href={d} underline="none">
                      <Box
                        display="flex"
                        justifyContent="space-between"
                        alignItems="center"
                        color="primary.main"
                      >
                        <Box>
                          <Typography component="h4" variant="h4" gutterBottom>
                            {d.substring(d.lastIndexOf("/") + 1)}
                          </Typography>
                          {/* <Typography variant="body2">Produktkatalog</Typography> */}
                        </Box>
                        <CustomIcon iconname="download" />
                      </Box>
                    </Link>
                  </Box>
                ))}
            </Box>
          </Box>
        </Grid>
        <Grid item md={6}>
          <Box pl={{ xs: 0, md: 7 }} pr={{ xs: 0, md: 7 }}>
            <Box mb={{ xs: 2, md: 3 }}>
              <Box mb={{ xs: 3, md: 3 }}>
                <Typography component="h3" variant="subtitle1" color="primary">
                  {formatMessage({
                    id: "MeetingLobbyComment",
                    defaultMessage: "Anliegen präzisieren",
                  })}
                </Typography>
              </Box>
              <MeetingProductAccordion
                locale={locale}
                productName={productName}
                productDescription={productDescription}
              />
            </Box>
            {viewAs === ViewAs.Expert && comment ? (
              <Typography variant="body1" color="primary">
                {comment}
              </Typography>
            ) : (
              viewAs === ViewAs.ActiveParticipant && (
                <TextField
                  placeholder={formatMessage({
                    id: "MeetingLobbyCommentPlaceholder",
                    defaultMessage: "bitte eingeben",
                  })}
                  onBlur={(e) => onCommentChange(e.target.value)}
                  multiline
                  variant="filled"
                  rows={4}
                  fullWidth
                >{comment}</TextField>
              )
            )}

            <Box
              display="flex"
              flexDirection={{ xs: "column-reverse", md: "row" }}
              justifyContent="flex-end"
              pt={{ xs: 5, md: 7.5 }}
            >
              {/* @SEE https://elbkapitaene.atlassian.net/browse/HMCMM-117 */}
              {/*<Box*/}
              {/*  display={{ xs: "block", md: "inline-block" }}*/}
              {/*  mt={{ xs: 1.5, md: 0 }}*/}
              {/*>*/}
              {/*  <Button*/}
              {/*    variant="text"*/}
              {/*    startIcon={<CustomIcon iconname="delete" />}*/}
              {/*    fullWidth*/}
              {/*    onClick={onCancel}*/}
              {/*  >*/}
              {/*    {formatMessage({*/}
              {/*      id: "MeetingLobbyCancel",*/}
              {/*      defaultMessage: "Termin absagen",*/}
              {/*    })}*/}
              {/*  </Button>*/}
              {/*</Box>*/}
              <Box>
                <Button onClick={onConfirm} fullWidth>
                  {formatMessage({
                    id: "MeetingLobbyConfirm",
                    defaultMessage: "Jetzt teilnehmen",
                  })}
                </Button>
              </Box>
            </Box>
          </Box>
        </Grid>
      </Grid>
    </Box>
  );
};
